import React, { useState, useCallback } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { useSMCSnackbar, useBoolean } from "../../../../../utils/hooks";
import SMCLink from "../../../../../components/Simple/SMCLink";
import EditorLayout from "../../../../../components/Layouts/EditorLayout";
import SMCLabel from "../../../../../components/Simple/SMCLabel";
import ButtonContainer from "../../../../../components/Simple/ButtonContainer";
import SMCButton from "../../../../../components/Simple/SMCButton";
import SMCTable from "../../../../../components/core/SMCTable/SMCTable";
import JoinCommunityModal from "../../../../PopupPages/JoinCommunityModal";
import { SMCCheckbox } from "../../../../../components/Simple/SMCCheckbox";
import SMCTextInput from "../../../../../components/Simple/SMCTextInput";
import Indentation from "../../../../../components/Simple/Indentation";
import { GatewayGetSchema } from "../../../../../api/DBSchema/Gateway";
import { SMCPaths } from "../../../../../conf/Routes";
import SMCIcon from "../../../../../components/Simple/SMCIcon";
import { push } from "connected-react-router";
import Icon from "@chkp/ci-front-common-icons";

const pageSchema = GatewayGetSchema.pages.vpnCommunity;
const pageId = pageSchema.pageId;

function BaseVpnCommunity({
  pageData,
  setObjectData,
  pageDisabled,
  isGateway,
  isGatewayExternal,
  navigateTo,
}) {
  const { t } = useTranslation();
  const { enqueueErrorSnackbar } = useSMCSnackbar();

  const communities = pageData[pageSchema.communityDataList];
  const useDnsName = pageData[pageSchema.useDnsName];
  const noLocalConnsEncrypt = pageData[pageSchema.noLocalConnsEncrypt];
  const dnsName = pageData[pageSchema.dnsName];
  const defaultDNSName = pageData[pageSchema.defaultDNSName];

  const copyDNS = useCallback(() => {
    navigator.clipboard.writeText(dnsName);
  }, [dnsName]);

  const setPageData = useCallback(
    (paramName, newValue) => {
      if (!pageDisabled) {
        setObjectData([pageId, paramName], newValue);
      }
    },
    [pageDisabled, setObjectData]
  );

  const [
    isChooseCommunityModalOpen,
    openChooseCommunityModal,
    closeChooseCommunityModal,
  ] = useBoolean(false);
  const [selectedObjects, setSelectedObjects] = useState([]);
  const [editObject, setEditObject] = useState(null);
  const [editObjectIndex, setEditObjectIndex] = useState(null);

  const deleteSelectedCommunites = useCallback(() => {
    if (communities && communities.length && selectedObjects.length) {
      const newCommunitiesObject = communities.filter(
        (comm) =>
          !selectedObjects.includes(comm[pageSchema.communityDataSchema.name])
      );
      setSelectedObjects([]);
      setPageData(pageSchema.communityDataList, newCommunitiesObject);
    }
  }, [communities, selectedObjects, setPageData]);

  const communityChooserOnCancel = useCallback(() => {
    // Set edit object and edit object key to null, to signify
    setEditObject(null);
    setEditObjectIndex(null);
    closeChooseCommunityModal();
  }, [closeChooseCommunityModal]);

  const addCommunityCallback = useCallback(
    (newCommunityObject) => {
      if (
        communities &&
        communities.find(
          (comm) =>
            comm[pageSchema.communityDataSchema.name] ===
            newCommunityObject.name
        )
      ) {
        enqueueErrorSnackbar(
          'Community "' + newCommunityObject.name + '" is already added.',
          {
            anchorOrigin: {
              horizontal: "center",
              vertical: "bottom",
            },
          }
        );
      } else {
        const _communities = communities ? [...communities] : [];
        _communities.push({
          [pageSchema.communityDataSchema.name]: newCommunityObject.name,
          [pageSchema.communityDataSchema.isCenter]:
            newCommunityObject.isCenter,
        });
        setPageData(pageSchema.communityDataList, _communities);
        closeChooseCommunityModal();
      }
    },
    [communities, closeChooseCommunityModal, enqueueErrorSnackbar, setPageData]
  );

  const editCommunityCallback = useCallback(
    (newCommunityObject) => {
      // If name changed, check if that name exists already
      if (
        communities[editObjectIndex][pageSchema.communityDataSchema.name] !==
          newCommunityObject.name &&
        communities &&
        communities.find(
          (comm) =>
            comm[pageSchema.communityDataSchema.name] ===
            newCommunityObject.name
        )
      ) {
        enqueueErrorSnackbar(
          'The ID "' + newCommunityObject.name + '" is already in use.',
          {
            anchorOrigin: {
              horizontal: "center",
              vertical: "bottom",
            },
          }
        );
      } else {
        const _communities = communities ? [...communities] : [];
        _communities[editObjectIndex] = {
          [pageSchema.communityDataSchema.name]: newCommunityObject.name,
          [pageSchema.communityDataSchema.isCenter]:
            newCommunityObject.isCenter,
        };
        setPageData(pageSchema.communityDataList, _communities);
        setEditObject(null);
        setEditObjectIndex(null);
        closeChooseCommunityModal();
      }
    },
    [
      communities,
      closeChooseCommunityModal,
      editObjectIndex,
      enqueueErrorSnackbar,
      setPageData,
    ]
  );

  const NameCell = React.memo(({ value, object, index }) => {
    return (
      <span>
        <SMCLink
          inline
          disabled={pageDisabled}
          onClick={() => {
            setEditObject(object);
            setEditObjectIndex(index);
            openChooseCommunityModal();
          }}
        >
          {value}
        </SMCLink>
      </span>
    );
  });

  const MemberTypeCell = React.memo(({ value }) => {
    return (
      <span>
        {value
          ? t("SMP.PLAN.SERVICES.VPN.COMMUNITY_WIZ.MEMBER_TYPE.CENTER_GATEWAY")
          : t("SMP.PLAN.SERVICES.VPN.COMMUNITY_WIZ.MEMBER_TYPE.NORMAL")}
      </span>
    );
  });

  const CommunityLinkCell = React.memo(({ object }) => {
    const communityName = object[pageSchema.communityDataSchema.name];
    return (
<SMCLink inline disabled={pageDisabled} onClick={() => navigateTo(`${SMCPaths.communities}?id=${communityName}`)}><SMCIcon icon="rightArrow" /> {t('SMC_GEN_GO')}</SMCLink>
    );
  });

  return (
    <EditorLayout>
      <SMCLabel bold>{t("SMP.GATEWAYS.PAGE.VPN.LINE1")}</SMCLabel>
      <ButtonContainer>
        <SMCButton
          variant="ghost"
          disabled={pageDisabled}
          icon="new"
          onClick={openChooseCommunityModal}
        >
          {t("SMC_GEN_NEW")}
        </SMCButton>
        <SMCButton
          variant="ghost"
          disabled={pageDisabled || !selectedObjects.length}
          icon="delete"
          onClick={deleteSelectedCommunites}
        >
          {t("SMC_GEN_DELETE")}
        </SMCButton>
      </ButtonContainer>
      <SMCTable
        columns={[
          {
            id: "name",
            accessor: pageSchema.communityDataSchema.name,
            header: t("SMP.NAME"),
            width: 200,
            Cell: NameCell,
          },
          {
            id: "isCenter",
            accessor: pageSchema.communityDataSchema.isCenter,
            header: t("SMP.GATEWAYS.PAGE.VPN.MEMBER_TYPE"),
            width: 155,
            Cell: MemberTypeCell,
          },
          {
            id: "communityLink",
            width: 50,
            align: "center",
            Cell: CommunityLinkCell,
          },
        ]}
        rowIdSelector={(obj) => obj[pageSchema.communityDataSchema.name]}
        selectedObjects={selectedObjects}
        setSelectedObjects={setSelectedObjects}
        hasCheckAll={true}
        content={communities}
        width={500}
        maxHeight={500}
      />
      <Indentation>
        {isGateway && !isGatewayExternal && (
          <>
            <SMCCheckbox
              disabled={pageDisabled}
              checked={useDnsName}
              onChange={(e) =>
                setPageData(pageSchema.useDnsName, e.target.checked)
              }
              label={t("SMP.GATEWAYS.PAGE.VPN.USE_DNS_NAME")}
              tooltip={t("SMP.GATEWAYS.PAGE.VPN.USE_DNS_NAME.TOOLTIP")}
            />
            <Indentation>
              <ButtonContainer>
                <SMCTextInput
                  width={400}
                  value={dnsName}
                  onChange={(e) =>
                    setPageData(pageSchema.dnsName, e.target.value)
                  }
                  disabled={pageDisabled || !useDnsName}
                  style={
                    dnsName === defaultDNSName
                      ? { fontStyle: "italic", color: "grey" }
                      : {}
                  }
                />
                <SMCButton
                  onClick={() => {
                    setPageData(pageSchema.dnsName, defaultDNSName);
                  }}
                  tooltip="Reset to default"
                >
                  {t("Restore")}
                </SMCButton>
                <SMCButton tooltip="Copy" onClick={copyDNS}>
                  <Icon name={"copy"} width={15} height={15} />
                </SMCButton>
              </ButtonContainer>
            </Indentation>
            <SMCLabel>
              <b>Note: </b>If you select "Use DNS name", permanent VPN tunnels
              are turned off even if enabled in one of its parent communities
            </SMCLabel>
          </>
        )}
        {!isGatewayExternal && (
          <SMCCheckbox
            checked={noLocalConnsEncrypt}
            onChange={(e) =>
              setPageData(pageSchema.noLocalConnsEncrypt, e.target.checked)
            }
            disabled={pageDisabled}
            label={t("SMP.PLAN.SERVICES.COMMUNITY.LINE2")}
            tooltip={t("SMP.PLAN.SERVICES.COMMUNITY.LINE3")}
          />
        )}
      </Indentation>
      <JoinCommunityModal
        isOpen={isChooseCommunityModalOpen}
        onSuccess={editObject ? editCommunityCallback : addCommunityCallback}
        onRequestClose={communityChooserOnCancel}
        defaultObject={editObject}
        isGateway={isGateway}
      />
    </EditorLayout>
  );
}

const mapStateToProps = null;

const mapDispatchToProps = {
  navigateTo: push,
};

export default connect(mapStateToProps, mapDispatchToProps)(BaseVpnCommunity);

